/*
 * Build-a-Box custom element (assets/build-a-box-branded.js).
 *
 * Drives the Build-a-Box hub (sections/main-build-a-box-branded.liquid). Each
 * "box" block emits a JSON payload of eligible products, already filtered on
 * the server to the variants that match the box unit (e.g. Count = "1 x 24 CT").
 * This element renders the sub-tab filters, the product grid with add / stepper
 * controls, and the box rail; enforces the box rules (min, step, max); handles
 * one-time vs subscribe (native Shopify selling plans, managed by Recharge);
 * and submits the box to the cart in one request.
 *
 * Cart modes (block setting `cart_mode`):
 *  - plain:    N ordinary lines joined by _bab / _bab_id / _bab_step properties.
 *  - recharge: N lines shaped for a Recharge dynamically priced customizable
 *              bundle: _rc_bundle "<nanoid>:<bundleProductId>", _rc_bundle_variant,
 *              _rc_bundle_parent (bundle handle), _rc_bundle_collection_id.
 *              Generated by the Recharge storefront SDK (recharge.bundle
 *              .getDynamicBundleItems) when it loads, otherwise by the documented
 *              fallback below. Needs the block's Bundle product (Recharge parent),
 *              whose variants are the box sizes ("4 Pods", "8 Pods", ...).
 *
 * The submit posts /cart/add with an `items` array and the `sections` param,
 * then hands the response to Dawn's cart-drawer.renderContents and publishes
 * PUB_SUB_EVENTS.cartUpdate, the same path product-form.js takes.
 */
if (!customElements.get('build-a-box')) {
  const NANO_ALPHABET = 'useandom-26T198340PX75pxJACKVERYMINDBUSHWOLF_GQZbfghjklqvwyzrict';
  const nanoId = (size = 10) => {
    const bytes = new Uint8Array(size);
    window.crypto.getRandomValues(bytes);
    let id = '';
    for (let i = 0; i < size; i += 1) id += NANO_ALPHABET[bytes[i] & 63];
    return id;
  };
  const esc = (s) =>
    String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const safeJson = (text) => {
    try {
      return JSON.parse(text);
    } catch (e) {
      return null;
    }
  };
  const leadingInt = (s) => {
    const m = String(s || '').match(/\d+/);
    return m ? parseInt(m[0], 10) : null;
  };
  const fill = (tpl, vars) => String(tpl || '').replace(/\[(\w+)\]/g, (_, k) => (vars[k] == null ? '' : vars[k]));

  class BuildABox extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.sdkUrl = this.dataset.sdkUrl || '';
      this.strings = safeJson(this.querySelector('[data-bab-strings]')?.textContent) || {};
      this.boxes = [];

      this.querySelectorAll('script[data-bab-box]').forEach((script) => {
        const data = safeJson(script.textContent);
        const handle = script.dataset.babBox;
        const panel = this.querySelector(`[data-bab-panel="${CSS.escape(handle)}"]`);
        if (!data || !panel) return;
        const box = this.buildBox(handle, data, panel);
        this.boxes.push(box);
        this.initBox(box);
      });

      this.tabs = Array.from(this.querySelectorAll('[data-bab-tab]'));
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.activate(tab.dataset.babTab, true));
        tab.addEventListener('keydown', (evt) => this.onTabKeydown(evt, tab));
      });

      const fromHash = (window.location.hash || '').replace('#', '');
      const initial = this.boxes.find((b) => b.handle === fromHash) || this.boxes[0];
      if (initial) this.activate(initial.handle, false);
      window.addEventListener('hashchange', () => {
        const h = (window.location.hash || '').replace('#', '');
        if (this.boxes.some((b) => b.handle === h)) this.activate(h, false);
      });

      // Link each box to the most recent box of its kind already in the cart
      // (edit mode), and keep that link fresh when the drawer changes the cart.
      this.syncFromCart();
      // Dawn's PUB_SUB_EVENTS is a top-level const (not a window property), so
      // probe it with typeof rather than window.PUB_SUB_EVENTS.
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, (evt) => {
          if (evt && evt.source === 'build-a-box') return;
          this.syncFromCart();
        });
      }
    }

    // ----- Box setup ------------------------------------------------------

    buildBox(handle, data, panel) {
      const cfg = Object.assign(
        { step: 4, min: 4, max: 24, noun: 'items', nounOne: 'item', mode: 'plain', allowSubscription: true, discountPct: 0, discountTiers: [], sizes: [], subtabs: [] },
        data.cfg || {}
      );
      cfg.step = Math.max(1, parseInt(cfg.step, 10) || 1);
      cfg.min = Math.max(1, parseInt(cfg.min, 10) || cfg.step);
      cfg.max = Math.max(cfg.min, parseInt(cfg.max, 10) || cfg.min);
      cfg.sizes = (Array.isArray(cfg.sizes) ? cfg.sizes : [])
        .map((z) => parseInt(z, 10) || 0)
        .filter((z) => z > 0)
        .sort((a, b) => a - b)
        .filter((z, i, all) => i === 0 || z !== all[i - 1]);
      if (!cfg.sizes.length) {
        for (let z = cfg.min; z <= cfg.max; z += cfg.step) cfg.sizes.push(z);
      }
      cfg.min = cfg.sizes[0];
      cfg.max = cfg.sizes[cfg.sizes.length - 1];
      cfg.discountPct = parseFloat(cfg.discountPct) || 0;
      cfg.discountTiers = (Array.isArray(cfg.discountTiers) ? cfg.discountTiers : [])
        .map((t) => ({ min: parseInt(t && t.min, 10) || 0, pct: parseFloat(t && t.pct) || 0 }))
        .filter((t) => t.min > 0 && t.pct > 0)
        .sort((a, b) => a.min - b.min);

      const products = (data.products || [])
        .filter((p) => p && Array.isArray(p.v) && p.v.length)
        .sort((a, b) => String(a.t).localeCompare(String(b.t)));
      const byPid = new Map();
      const byVid = new Map();
      products.forEach((p) => {
        p.plans = Array.isArray(p.plans) ? p.plans : [];
        p.planNames = new Set(p.plans.map((pl) => pl.n));
        byPid.set(p.id, p);
        p.v.forEach((v) => byVid.set(v.id, { p, v }));
      });

      const q = (sel) => panel.querySelector(sel);
      const box = {
        handle,
        cfg,
        bundle: data.bundle || null,
        products,
        byPid,
        byVid,
        panel,
        state: this.loadState(handle) || { sel: {}, order: [], purchase: 'onetime', freq: null, tab: 'all', q: '' },
        chosenVariant: {}, // pid -> vid currently shown in the tile select
        editing: null, // { id, missing } while the rail mirrors a box already in the cart
        cartBox: null, // { id, lines } the most recent box of this kind in the cart
        el: {
          grid: q('[data-bab-grid]'),
          subtabs: q('[data-bab-subtabs]'),
          search: q('[data-bab-search]'),
          empty: q('[data-bab-empty]'),
          rail: q('[data-bab-rail]'),
          toggle: q('[data-bab-toggle]'),
          count: q('[data-bab-count]'),
          next: q('[data-bab-next]'),
          barTotal: q('[data-bab-bar-total]'),
          status: q('[data-bab-status]'),
          fill: q('[data-bab-fill]'),
          list: q('[data-bab-list]'),
          purchase: q('[data-bab-purchase]'),
          purchaseOpts: Array.from(panel.querySelectorAll('[data-bab-purchase-opt]')),
          onetimeTotal: q('[data-bab-onetime-total]'),
          subTotal: q('[data-bab-sub-total]'),
          subBadge: q('[data-bab-sub-badge]'),
          freqWrap: q('[data-bab-freq-wrap]'),
          freq: q('[data-bab-freq]'),
          subNote: q('[data-bab-sub-note]'),
          subtotal: q('[data-bab-subtotal]'),
          subSavingsRow: q('[data-bab-sub-savings-row]'),
          subSavings: q('[data-bab-sub-savings]'),
          boxSavingsRow: q('[data-bab-box-savings-row]'),
          boxSavings: q('[data-bab-box-savings]'),
          boxSavingsLabel: q('[data-bab-box-savings-label]'),
          totalRow: q('[data-bab-total-row]'),
          total: q('[data-bab-total]'),
          submit: q('[data-bab-submit]'),
          submitText: q('[data-bab-submit-text]'),
          hint: q('[data-bab-hint]'),
          message: q('[data-bab-message]'),
          error: q('[data-bab-error]'),
          clear: q('[data-bab-clear]'),
          banner: q('[data-bab-banner]'),
          bannerText: q('[data-bab-banner-text]'),
          bannerAction: q('[data-bab-banner-action]'),
        },
      };

      // Drop remembered selections that no longer resolve (product left the
      // collection, variant deleted) and clamp to the max.
      const sel = {};
      const order = [];
      let total = 0;
      (box.state.order || Object.keys(box.state.sel || {})).forEach((vidKey) => {
        const vid = parseInt(vidKey, 10);
        const qty = parseInt(box.state.sel[vidKey], 10) || 0;
        if (!byVid.has(vid) || qty <= 0) return;
        const allowed = Math.max(0, Math.min(qty, cfg.max - total));
        if (!allowed) return;
        sel[vid] = allowed;
        order.push(vid);
        total += allowed;
      });
      box.state.sel = sel;
      box.state.order = order;
      if (!cfg.allowSubscription) box.state.purchase = 'onetime';
      return box;
    }

    initBox(box) {
      const { el, cfg } = box;
      el.submitDefault = el.submitText ? el.submitText.textContent.trim() : '';
      this.buildSubtabs(box);
      if (el.search) {
        el.search.value = box.state.q || '';
        el.search.addEventListener('input', () => {
          box.state.q = el.search.value;
          this.renderGrid(box);
        });
      }

      // Grid interactions (delegated so re-renders need no rebinding).
      el.grid.addEventListener('click', (evt) => {
        const btn = evt.target.closest('button');
        if (!btn || !el.grid.contains(btn)) return;
        const tile = btn.closest('[data-pid]');
        if (!tile) return;
        const pid = parseInt(tile.dataset.pid, 10);
        if (btn.hasAttribute('data-bab-add') || btn.hasAttribute('data-bab-plus')) {
          this.changeQty(box, this.tileVariant(box, pid), +1);
        } else if (btn.hasAttribute('data-bab-minus')) {
          this.changeQty(box, this.tileMinusVariant(box, pid), -1);
        }
      });
      el.grid.addEventListener('change', (evt) => {
        const select = evt.target.closest('[data-bab-variant]');
        if (!select) return;
        const tile = select.closest('[data-pid]');
        const pid = parseInt(tile.dataset.pid, 10);
        box.chosenVariant[pid] = parseInt(select.value, 10);
        this.updateTile(box, pid);
      });

      // Rail interactions.
      el.list.addEventListener('click', (evt) => {
        const btn = evt.target.closest('button');
        if (!btn) return;
        const line = btn.closest('[data-vid]');
        if (!line) return;
        const vid = parseInt(line.dataset.vid, 10);
        if (btn.hasAttribute('data-bab-line-plus')) this.changeQty(box, vid, +1);
        else if (btn.hasAttribute('data-bab-line-minus')) this.changeQty(box, vid, -1);
        else if (btn.hasAttribute('data-bab-line-remove')) this.changeQty(box, vid, -(box.state.sel[vid] || 0));
      });
      el.purchaseOpts.forEach((radio) => {
        radio.addEventListener('change', () => {
          box.state.purchase = radio.value;
          this.saveState(box);
          this.renderRail(box);
          this.refreshTilePrices(box);
        });
      });
      el.freq?.addEventListener('change', () => {
        box.state.freq = el.freq.value;
        this.saveState(box);
        this.renderRail(box);
      });
      el.submit?.addEventListener('click', () => this.submit(box));
      el.clear?.addEventListener('click', () => this.exitEdit(box));
      el.bannerAction?.addEventListener('click', () => {
        if (el.bannerAction.dataset.action === 'load' && box.cartBox) this.enterEdit(box, box.cartBox);
        else this.exitEdit(box);
      });
      el.toggle?.addEventListener('click', () => {
        const open = !el.rail.classList.contains('bab-rail--open');
        el.rail.classList.toggle('bab-rail--open', open);
        el.toggle.setAttribute('aria-expanded', String(open));
      });

      // Reflect the remembered purchase option.
      el.purchaseOpts.forEach((r) => {
        r.checked = r.value === box.state.purchase;
      });

      this.renderGrid(box);
      this.renderRail(box);
    }

    // ----- Tabs (boxes) ---------------------------------------------------

    activate(handle, pushHash) {
      this.boxes.forEach((box) => {
        const on = box.handle === handle;
        box.panel.hidden = !on;
      });
      this.tabs.forEach((tab) => {
        const on = tab.dataset.babTab === handle;
        tab.setAttribute('aria-selected', String(on));
        tab.tabIndex = on ? 0 : -1;
      });
      this.activeHandle = handle;
      if (pushHash && window.history && window.history.replaceState) {
        window.history.replaceState(null, '', `#${handle}`);
      }
    }

    onTabKeydown(evt, tab) {
      if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(evt.key)) return;
      evt.preventDefault();
      const i = this.tabs.indexOf(tab);
      let next = i;
      if (evt.key === 'ArrowRight') next = (i + 1) % this.tabs.length;
      if (evt.key === 'ArrowLeft') next = (i - 1 + this.tabs.length) % this.tabs.length;
      if (evt.key === 'Home') next = 0;
      if (evt.key === 'End') next = this.tabs.length - 1;
      this.tabs[next].focus();
      this.activate(this.tabs[next].dataset.babTab, true);
    }

    // ----- Sub-tabs (tag filters) ----------------------------------------

    buildSubtabs(box) {
      const { el, cfg } = box;
      if (!el.subtabs) return;
      const tabs = [{ l: 'All', t: 'all' }].concat(
        (cfg.subtabs || []).filter((s) => s && s.l && s.t && box.products.some((p) => (p.tags || []).includes(s.t)))
      );
      if (!tabs.some((s) => s.t === box.state.tab)) box.state.tab = 'all';
      el.subtabs.innerHTML = tabs
        .map(
          (s) =>
            `<button type="button" class="bab-subtab" data-tag="${esc(s.t)}" aria-pressed="${s.t === box.state.tab}">${esc(s.l)}</button>`
        )
        .join('');
      el.subtabs.addEventListener('click', (evt) => {
        const btn = evt.target.closest('[data-tag]');
        if (!btn) return;
        box.state.tab = btn.dataset.tag;
        el.subtabs.querySelectorAll('[data-tag]').forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
        this.saveState(box);
        this.renderGrid(box);
      });
    }

    visibleProducts(box) {
      const tag = box.state.tab || 'all';
      const q = (box.state.q || '').trim().toLowerCase();
      return box.products.filter((p) => {
        if (tag !== 'all' && !(p.tags || []).includes(tag)) return false;
        if (q && !String(p.t).toLowerCase().includes(q)) return false;
        return true;
      });
    }

    // ----- Grid -----------------------------------------------------------

    renderGrid(box) {
      const { el } = box;
      const list = this.visibleProducts(box);
      el.grid.innerHTML = list.map((p) => this.tileHTML(box, p)).join('');
      if (el.empty) el.empty.hidden = list.length > 0;
    }

    // The variant the tile's select points at (or the only matching variant).
    tileVariant(box, pid) {
      const p = box.byPid.get(pid);
      if (!p) return null;
      const chosen = box.chosenVariant[pid];
      if (chosen && p.v.some((v) => v.id === chosen)) return chosen;
      const first = p.v.find((v) => v.a) || p.v[0];
      box.chosenVariant[pid] = first.id;
      return first.id;
    }

    // Minus on a tile removes from the shown variant when it has quantity,
    // otherwise from the most recently added variant of that product.
    tileMinusVariant(box, pid) {
      const shown = this.tileVariant(box, pid);
      if ((box.state.sel[shown] || 0) > 0) return shown;
      const p = box.byPid.get(pid);
      const owned = box.state.order.filter((vid) => p.v.some((v) => v.id === vid) && box.state.sel[vid] > 0);
      return owned.length ? owned[owned.length - 1] : shown;
    }

    productQty(box, pid) {
      const p = box.byPid.get(pid);
      return p ? p.v.reduce((n, v) => n + (box.state.sel[v.id] || 0), 0) : 0;
    }

    variantLabel(box, p, v) {
      const parts = (v.o || []).filter((_, i) => i !== p.oi).filter((x) => x && x !== 'Default Title');
      return parts.join(' / ');
    }

    unitPrice(box, v) {
      return box.state.purchase === 'subscribe' && v.s != null ? v.s : v.p;
    }

    tileHTML(box, p) {
      const s = this.strings;
      const vid = this.tileVariant(box, p.id);
      const v = p.v.find((x) => x.id === vid) || p.v[0];
      const qty = this.productQty(box, p.id);
      const anyAvailable = p.v.some((x) => x.a);
      const multi = p.v.length > 1;
      const label = box.cfg.variantLabel || (p.opts || []).find((_, i) => i !== p.oi) || '';
      const select = multi
        ? `<div class="bab-tile__variant">
             <label class="visually-hidden" for="bab-v-${box.handle}-${p.id}">${esc(label)}</label>
             <div class="select">
               <select id="bab-v-${box.handle}-${p.id}" class="select__select" data-bab-variant>
                 ${p.v
                   .map(
                     (x) =>
                       `<option value="${x.id}"${x.id === vid ? ' selected' : ''}${x.a ? '' : ' disabled'}>${esc(this.variantLabel(box, p, x))}${x.a ? '' : ` (${esc(s.soldOut || 'Sold out')})`}</option>`
                   )
                   .join('')}
               </select>
               <span class="svg-wrapper"><svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-caret" viewBox="0 0 10 6"><path fill="currentColor" fill-rule="evenodd" d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708" clip-rule="evenodd"/></svg></span>
             </div>
           </div>`
        : '';
      const hasPlans = p.plans.length > 0;
      return `<li class="bab-tile${qty ? ' bab-tile--in-box' : ''}${anyAvailable ? '' : ' bab-tile--sold-out'}" data-pid="${p.id}" data-plans="${hasPlans ? 1 : 0}">
        <a class="bab-tile__media" href="${esc(p.u)}" tabindex="-1" aria-hidden="true">
          ${p.img ? `<img src="${esc(v.img || p.img)}" alt="" loading="lazy" width="300" height="300">` : '<span class="bab-tile__noimg"></span>'}
          <span class="bab-tile__badge" data-bab-tile-badge${qty ? '' : ' hidden'}>${qty}</span>
          ${!hasPlans && box.cfg.allowSubscription ? `<span class="bab-tile__onetime">${esc(s.onetimeOnly || 'One-time only')}</span>` : ''}
        </a>
        <div class="bab-tile__body">
          <h3 class="bab-tile__title"><a href="${esc(p.u)}">${esc(p.t)}</a></h3>
          <p class="bab-tile__price">
            <span class="bab-tile__price-now" data-bab-tile-price>${this.formatMoney(this.unitPrice(box, v))}</span>
            ${v.c && v.c > v.p ? `<s class="bab-tile__price-was">${this.formatMoney(v.c)}</s>` : ''}
            <span class="bab-tile__price-each">${esc(s.perUnit || 'each')}</span>
          </p>
          ${select}
          <div class="bab-tile__actions" data-bab-tile-actions>${this.tileActionsHTML(box, p, v, qty)}</div>
        </div>
      </li>`;
    }

    tileActionsHTML(box, p, v, qty) {
      const s = this.strings;
      if (!v || !v.a) {
        return `<button type="button" class="button button--secondary bab-tile__add" disabled>${esc(s.soldOut || 'Sold out')}</button>`;
      }
      if (!qty) {
        return `<button type="button" class="button button--primary bab-tile__add" data-bab-add aria-label="${esc(s.add || 'Add to box')}: ${esc(p.t)}">${esc(s.add || 'Add to box')}</button>`;
      }
      return `<div class="bab-stepper" role="group" aria-label="${esc(p.t)}">
        <button type="button" class="bab-stepper__btn" data-bab-minus aria-label="Remove one ${esc(p.t)}">&minus;</button>
        <span class="bab-stepper__count" aria-live="polite">${qty}</span>
        <button type="button" class="bab-stepper__btn" data-bab-plus aria-label="Add one more ${esc(p.t)}">+</button>
      </div>`;
    }

    updateTile(box, pid) {
      const tile = box.el.grid.querySelector(`[data-pid="${pid}"]`);
      if (!tile) return;
      const p = box.byPid.get(pid);
      const vid = this.tileVariant(box, pid);
      const v = p.v.find((x) => x.id === vid) || p.v[0];
      const qty = this.productQty(box, pid);
      tile.classList.toggle('bab-tile--in-box', qty > 0);
      const badge = tile.querySelector('[data-bab-tile-badge]');
      if (badge) {
        badge.textContent = qty;
        badge.hidden = !qty;
      }
      const price = tile.querySelector('[data-bab-tile-price]');
      if (price) price.textContent = this.formatMoney(this.unitPrice(box, v));
      const img = tile.querySelector('img');
      if (img && (v.img || p.img)) img.src = v.img || p.img;
      const actions = tile.querySelector('[data-bab-tile-actions]');
      if (actions) actions.innerHTML = this.tileActionsHTML(box, p, v, qty);
    }

    refreshTilePrices(box) {
      box.el.grid.querySelectorAll('[data-pid]').forEach((tile) => {
        const pid = parseInt(tile.dataset.pid, 10);
        const p = box.byPid.get(pid);
        const vid = this.tileVariant(box, pid);
        const v = p.v.find((x) => x.id === vid) || p.v[0];
        const price = tile.querySelector('[data-bab-tile-price]');
        if (price) price.textContent = this.formatMoney(this.unitPrice(box, v));
      });
    }

    // ----- Selection state -----------------------------------------------

    totalUnits(box) {
      return Object.values(box.state.sel).reduce((n, q) => n + (q || 0), 0);
    }

    changeQty(box, vid, delta) {
      if (!vid || !box.byVid.has(vid)) return;
      const { cfg } = box;
      const total = this.totalUnits(box);
      const current = box.state.sel[vid] || 0;
      let next = current + delta;
      if (delta > 0 && total >= cfg.max) {
        this.flashHint(box, fill(this.strings.maxReached, { max: cfg.max, noun: cfg.noun }));
        return;
      }
      if (delta > 0 && !box.byVid.get(vid).v.a) return;
      next = Math.max(0, Math.min(next, current + (cfg.max - total)));
      if (next === current) return;
      if (next === 0) {
        delete box.state.sel[vid];
        box.state.order = box.state.order.filter((x) => x !== vid);
      } else {
        box.state.sel[vid] = next;
        if (!box.state.order.includes(vid)) box.state.order.push(vid);
      }
      this.hideMessages(box);
      this.saveState(box);
      this.updateTile(box, box.byVid.get(vid).p.id);
      this.renderRail(box);
    }

    // Plan names every selected product offers (empty when any selected
    // product has no plans, which makes the whole box one-time only).
    commonPlanNames(box) {
      const lines = this.lines(box);
      if (!lines.length) return [];
      let names = null;
      for (const { p } of lines) {
        if (!p.plans.length) return [];
        const set = new Set(p.plans.map((pl) => pl.n));
        names = names ? names.filter((n) => set.has(n)) : Array.from(set);
      }
      return (names || []).sort((a, b) => (leadingInt(a) || 0) - (leadingInt(b) || 0));
    }

    lines(box) {
      return box.state.order
        .filter((vid) => box.state.sel[vid] > 0 && box.byVid.has(vid))
        .map((vid) => Object.assign({ qty: box.state.sel[vid] }, box.byVid.get(vid)));
    }

    // ----- Rail -----------------------------------------------------------

    // The box discount is tiered: a bigger box earns a bigger percentage. Recharge
    // owns the real numbers, so this only mirrors them for the rail. Tiers are
    // sorted ascending, so the last one the count clears is the one that applies.
    discountFor(cfg, n) {
      if (!cfg.discountTiers.length) return cfg.discountPct;
      let pct = 0;
      cfg.discountTiers.forEach((t) => {
        if (n >= t.min) pct = t.pct;
      });
      return pct;
    }

    renderRail(box) {
      const { el, cfg } = box;
      const s = this.strings;
      const n = this.totalUnits(box);
      const lines = this.lines(box);
      const nextTarget = n === 0 ? cfg.min : cfg.sizes.find((z) => z >= n) || cfg.max;
      const complete = cfg.sizes.includes(n);
      const nounFor = (k) => (k === 1 ? cfg.nounOne : cfg.noun);

      // Subscription availability.
      const planNames = cfg.allowSubscription ? this.commonPlanNames(box) : [];
      const blocker = cfg.allowSubscription && lines.length ? lines.find(({ p }) => !p.plans.length) : null;
      const canSubscribe = planNames.length > 0;
      if (!canSubscribe && box.state.purchase === 'subscribe') {
        box.state.purchase = 'onetime';
        el.purchaseOpts.forEach((r) => {
          r.checked = r.value === 'onetime';
        });
      }
      const subscribing = box.state.purchase === 'subscribe' && canSubscribe;
      if (canSubscribe) {
        if (!box.state.freq || !planNames.includes(box.state.freq)) {
          box.state.freq =
            planNames.find((x) => cfg.defaultFrequency && x.toLowerCase() === String(cfg.defaultFrequency).toLowerCase()) ||
            planNames.find((x) => /\b4\b/.test(x)) ||
            planNames[0];
        }
        if (el.freq) {
          el.freq.innerHTML = planNames.map((x) => `<option value="${esc(x)}"${x === box.state.freq ? ' selected' : ''}>${esc(x)}</option>`).join('');
        }
      }

      // Totals.
      const subtotal = lines.reduce((sum, { v, qty }) => sum + v.p * qty, 0);
      const subTotal = lines.reduce((sum, { v, qty }) => sum + (v.s != null ? v.s : v.p) * qty, 0);
      const base = subscribing ? subTotal : subtotal;
      const discountPct = complete ? this.discountFor(cfg, n) : 0;
      const boxSavings = discountPct > 0 ? Math.round((base * discountPct) / 100) : 0;
      const total = base - boxSavings;

      // Header counters.
      el.count.textContent = n;
      el.next.textContent = nextTarget;
      if (el.barTotal) el.barTotal.textContent = n ? this.formatMoney(total) : '';
      el.rail.classList.toggle('bab-rail--complete', complete);
      el.rail.classList.toggle('bab-rail--has-items', n > 0);

      // Status + progress.
      let status;
      if (n === 0) status = fill(s.statusEmpty, { min: cfg.min, noun: nounFor(cfg.min) });
      else if (!complete) status = fill(s.statusIncomplete, { remaining: nextTarget - n, noun: nounFor(nextTarget - n) });
      else {
        const bigger = cfg.sizes.find((z) => z > n);
        status = `${fill(s.statusComplete, { count: n, noun: nounFor(n) })} ${bigger ? fill(s.statusMore, { step: bigger - n, max: cfg.max }) : s.statusMax || ''}`;
      }
      el.status.textContent = status.trim();
      const floorSize = cfg.sizes.filter((z) => z < nextTarget).pop() || 0;
      const pct = n === 0 ? 0 : complete ? 100 : ((n - floorSize) / (nextTarget - floorSize)) * 100;
      el.fill.style.width = `${pct}%`;

      // Lines + placeholder slots up to the next full box.
      const slots = Math.max(0, nextTarget - n);
      el.list.innerHTML =
        lines
          .map(({ p, v, qty }) => {
            const meta = [this.variantLabel(box, p, v), `${this.formatMoney(this.unitPrice(box, v))} ${esc(s.perUnit || 'each')}`].filter(Boolean).join(' &middot; ');
            return `<li class="bab-line" data-vid="${v.id}">
              ${v.img || p.img ? `<img class="bab-line__img" src="${esc(v.img || p.img)}" alt="" width="96" height="96" loading="lazy">` : '<span class="bab-line__img bab-line__img--empty"></span>'}
              <span class="bab-line__info">
                <span class="bab-line__title">${esc(p.t)}</span>
                <span class="bab-line__meta">${meta}</span>
              </span>
              <span class="bab-line__qty" role="group" aria-label="${esc(p.t)}">
                <button type="button" class="bab-stepper__btn" data-bab-line-minus aria-label="Remove one ${esc(p.t)}">&minus;</button>
                <span class="bab-stepper__count">${qty}</span>
                <button type="button" class="bab-stepper__btn" data-bab-line-plus aria-label="Add one more ${esc(p.t)}">+</button>
              </span>
              <button type="button" class="bab-line__remove" data-bab-line-remove aria-label="Remove ${esc(p.t)} from box">&times;</button>
            </li>`;
          })
          .join('') +
        Array.from({ length: slots })
          .map(
            () =>
              `<li class="bab-line bab-line--empty" aria-hidden="true"><span class="bab-line__slot">+</span><span class="bab-line__info"><span class="bab-line__title">${esc(s.emptySlot || 'Select a product')}</span></span></li>`
          )
          .join('');

      // Purchase options.
      if (el.purchase && cfg.allowSubscription) {
        el.onetimeTotal.textContent = n ? this.formatMoney(subtotal) : '';
        el.subTotal.textContent = n && canSubscribe ? this.formatMoney(subTotal) : '';
        const subPct = subtotal > 0 && subTotal < subtotal ? Math.round(((subtotal - subTotal) / subtotal) * 100) : 0;
        el.subBadge.hidden = !(n && canSubscribe && subPct > 0);
        el.subBadge.textContent = subPct > 0 ? `Save ${subPct}%` : '';
        const subRadio = el.purchaseOpts.find((r) => r.value === 'subscribe');
        if (subRadio) subRadio.disabled = !canSubscribe || n === 0;
        el.freqWrap.hidden = !subscribing;
        if (blocker) {
          el.subNote.hidden = false;
          el.subNote.textContent = fill(s.subUnavailable, { title: blocker.p.t });
        } else {
          el.subNote.hidden = true;
          el.subNote.textContent = '';
        }
      }

      // Totals block.
      el.subtotal.textContent = this.formatMoney(subtotal);
      el.subSavingsRow.hidden = !(subscribing && subtotal - subTotal > 0);
      el.subSavings.textContent = `-${this.formatMoney(subtotal - subTotal)}`;
      el.boxSavingsRow.hidden = !(boxSavings > 0);
      el.boxSavings.textContent = `-${this.formatMoney(boxSavings)}`;
      if (el.boxSavingsLabel && boxSavings > 0) {
        el.boxSavingsLabel.textContent = `${cfg.discountLabel || 'Box discount'} (${discountPct}%, ${s.estimated || 'estimated'})`;
      }
      el.totalRow.hidden = !(n > 0 && total !== subtotal);
      el.total.textContent = this.formatMoney(total);

      // Cart link banner + CTA.
      this.renderBanner(box, n, nounFor);
      if (el.submitText) {
        el.submitText.textContent = box.editing ? s.update || 'Update box in cart' : el.submitDefault || el.submitText.textContent;
      }
      el.submit.disabled = !complete || this.busy;
      el.hint.textContent = n > 0 && !complete ? fill(s.statusIncomplete, { remaining: nextTarget - n, noun: nounFor(nextTarget - n) }) : '';
      el.clear.hidden = n === 0 || !!box.editing;
    }

    renderBanner(box, n, nounFor) {
      const { el, cfg } = box;
      const s = this.strings;
      if (!el.banner) return;
      if (box.editing) {
        let text = fill(s.editing, { count: n, noun: nounFor(n), label: cfg.label });
        if (box.editing.missing) text += ` ${fill(s.missing, { missing: box.editing.missing })}`;
        el.bannerText.textContent = text;
        el.bannerAction.textContent = s.editNew || 'Build a new box';
        el.bannerAction.dataset.action = 'new';
        el.banner.hidden = false;
      } else if (box.cartBox) {
        const count = box.cartBox.lines.reduce((sum, l) => sum + (parseInt(l.quantity, 10) || 0), 0);
        el.bannerText.textContent = fill(s.offer, { count, noun: nounFor(count), label: cfg.label });
        el.bannerAction.textContent = s.offerLoad || 'Edit that box';
        el.bannerAction.dataset.action = 'load';
        el.banner.hidden = false;
      } else {
        el.banner.hidden = true;
      }
    }

    flashHint(box, text) {
      const { el } = box;
      el.hint.textContent = text;
      el.hint.classList.add('bab-rail__hint--flash');
      clearTimeout(this.flashTimer);
      this.flashTimer = setTimeout(() => {
        el.hint.classList.remove('bab-rail__hint--flash');
        this.renderRail(box);
      }, 2500);
    }

    hideMessages(box) {
      box.el.message.hidden = true;
      box.el.error.hidden = true;
    }

    // ----- Cart submission -----------------------------------------------

    async submit(box) {
      const { el, cfg } = box;
      const s = this.strings;
      const n = this.totalUnits(box);
      if (this.busy || !cfg.sizes.includes(n)) return;
      this.busy = true;
      this.hideMessages(box);
      el.submit.classList.add('loading');
      el.submit.setAttribute('aria-disabled', 'true');
      el.submit.querySelector('.loading__spinner')?.classList.remove('hidden');

      try {
        const items = await this.buildItems(box);
        const body = {
          items,
          sections: this.cartSections(),
          sections_url: window.location.pathname,
        };
        const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
        const res = await fetch(addUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(body),
        });
        let data = await res.json();
        if (!res.ok || data.status) {
          throw new Error(data.description || data.message || s.error || 'Could not add to cart');
        }

        let confirmation = s.added || 'Box added to your cart.';
        if (box.editing) {
          // Editing a box already in the cart: the replacement lines are in
          // (new box id), so drop the old box's lines. The update call renders
          // the same sections the add call does, so the drawer gets the final state.
          const oldKeys = await this.currentBoxKeys(box.editing.id);
          if (oldKeys.length) {
            const updated = await this.removeCartLines(oldKeys, true);
            if (updated && updated.sections) data = Object.assign({}, data, { sections: updated.sections });
          }
          confirmation = s.updated || 'Box updated in your cart.';
        }

        // Same downstream path as product-form.js: notify + re-render the drawer.
        // A rendering hiccup must not leave the box state half-finalized, so
        // the drawer work is fenced off from the state reset below.
        try {
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'build-a-box', cartData: data });
          }
          const drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.renderContents === 'function' && data.sections) {
            // Dawn's product-form.js clears is-empty on the element before
            // re-rendering; without this the header and lines stay hidden when
            // the cart was empty a moment ago.
            drawer.classList.remove('is-empty');
            drawer.renderContents(data);
          } else if (window.routes && window.routes.cart_url) {
            window.location = window.routes.cart_url;
            return;
          }
        } catch (renderErr) {
          /* the cart is correct; the drawer simply did not repaint this time */
        }

        box.editing = null;
        box.state.editingId = null;
        box.state.sel = {};
        box.state.order = [];
        this.saveState(box);
        this.renderGrid(box);
        // The box just added is now "the last box in the cart": mirror it in
        // edit mode so a return visit (or an immediate tweak) starts from it.
        await this.syncFromCart(box);
        el.message.textContent = confirmation;
        el.message.hidden = false;
      } catch (err) {
        el.error.textContent = err && err.message ? err.message : s.error || 'Could not add to cart';
        el.error.hidden = false;
      } finally {
        this.busy = false;
        el.submit.classList.remove('loading');
        el.submit.removeAttribute('aria-disabled');
        el.submit.querySelector('.loading__spinner')?.classList.add('hidden');
        this.renderRail(box);
      }
    }

    cartSections() {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.getSectionsToRender === 'function') {
        return drawer
          .getSectionsToRender()
          .map((section) => section.id)
          .join(',');
      }
      return 'cart-drawer,cart-icon-bubble';
    }

    // ----- Boxes already in the cart (edit mode) -------------------------

    async fetchCart() {
      try {
        const res = await fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
        return res.ok ? await res.json() : null;
      } catch (e) {
        return null;
      }
    }

    // The shared id that joins a box's lines: Recharge's _rc_bundle, or our _bab_id.
    groupKey(line) {
      const p = (line && line.properties) || {};
      return p._rc_bundle || p._bab_id || null;
    }

    boxOwnsLine(box, line) {
      const p = (line && line.properties) || {};
      if (p._bab) return p._bab === box.handle;
      if (p._rc_bundle_parent) return !!(box.bundle && p._rc_bundle_parent === box.bundle.handle);
      return false;
    }

    // Cart lines come newest first, so the first owned line marks the last box built.
    findLastCartBox(cart, box) {
      for (const line of cart.items) {
        const key = this.groupKey(line);
        if (!key || !this.boxOwnsLine(box, line)) continue;
        return { id: key, lines: cart.items.filter((l) => this.groupKey(l) === key) };
      }
      return null;
    }

    async syncFromCart(only) {
      const cart = await this.fetchCart();
      if (!cart || !Array.isArray(cart.items)) return;
      (only ? [only] : this.boxes).forEach((box) => {
        box.cartBox = this.findLastCartBox(cart, box);
        if (box.cartBox && this.totalUnits(box) === 0) {
          // Nothing in progress: mirror the cart box.
          this.enterEdit(box, box.cartBox);
        } else if (box.cartBox && box.state.editingId === box.cartBox.id) {
          // Returning mid-edit (draft remembered from a previous visit): keep
          // the in-progress lines but stay in edit mode for that cart box.
          box.editing = box.editing && box.editing.id === box.cartBox.id ? box.editing : { id: box.cartBox.id, missing: 0 };
          this.renderRail(box);
        } else {
          // A box being edited that vanished from the cart becomes a plain draft.
          if (box.editing && (!box.cartBox || box.cartBox.id !== box.editing.id)) box.editing = null;
          if (!box.editing && box.state.editingId) {
            box.state.editingId = null;
            this.saveState(box);
          }
          this.renderRail(box);
        }
      });
    }

    // Mirror a cart box in the rail. Lines whose variant is no longer offered
    // (left the collection, deleted) are counted as missing and reported.
    enterEdit(box, found) {
      const { cfg, el } = box;
      const sel = {};
      const order = [];
      let missing = 0;
      let total = 0;
      let purchase = 'onetime';
      let freq = null;
      found.lines
        .slice()
        .reverse() // restore the order the shopper added them in
        .forEach((line) => {
          const vid = line.variant_id;
          const qty = parseInt(line.quantity, 10) || 0;
          if (!box.byVid.has(vid)) {
            missing += qty;
            return;
          }
          const allowed = Math.max(0, Math.min(qty, cfg.max - total));
          if (!allowed) {
            missing += qty;
            return;
          }
          sel[vid] = (sel[vid] || 0) + allowed;
          total += allowed;
          if (!order.includes(vid)) order.push(vid);
          box.chosenVariant[box.byVid.get(vid).p.id] = vid;
          if (line.selling_plan_allocation && line.selling_plan_allocation.selling_plan) {
            purchase = 'subscribe';
            freq = line.selling_plan_allocation.selling_plan.name;
          }
        });
      box.state.sel = sel;
      box.state.order = order;
      box.state.purchase = cfg.allowSubscription ? purchase : 'onetime';
      box.state.freq = freq;
      box.state.editingId = found.id;
      box.editing = { id: found.id, missing };
      el.purchaseOpts.forEach((r) => {
        r.checked = r.value === box.state.purchase;
      });
      this.hideMessages(box);
      this.saveState(box);
      this.renderGrid(box);
      this.renderRail(box);
    }

    exitEdit(box) {
      box.editing = null;
      box.state.editingId = null;
      box.state.sel = {};
      box.state.order = [];
      this.hideMessages(box);
      this.saveState(box);
      this.renderGrid(box);
      this.renderRail(box);
    }

    async currentBoxKeys(boxId) {
      const cart = await this.fetchCart();
      if (!cart || !Array.isArray(cart.items)) return [];
      return cart.items.filter((l) => this.groupKey(l) === boxId).map((l) => l.key);
    }

    // Remove cart lines by key. With withSections the response also carries the
    // re-rendered drawer sections (the Cart API's bundled section rendering),
    // exactly like the add call, so the drawer can be repainted from it.
    async removeCartLines(keys, withSections) {
      const updates = {};
      keys.forEach((k) => {
        updates[k] = 0;
      });
      const body = { updates };
      if (withSections) {
        body.sections = this.cartSections();
        body.sections_url = window.location.pathname;
      }
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(this.strings.error || 'Could not update the cart');
      return res.json();
    }

    async buildItems(box) {
      const { cfg } = box;
      const lines = this.lines(box);
      const subscribing = box.state.purchase === 'subscribe' && this.commonPlanNames(box).length > 0;
      const planFor = (p) => {
        if (!subscribing) return null;
        const plan = p.plans.find((pl) => pl.n === box.state.freq);
        return plan ? plan.id : null;
      };

      if (cfg.mode !== 'recharge') {
        const boxId = nanoId(12);
        return lines.map(({ p, v, qty }) => {
          const item = {
            id: v.id,
            quantity: qty,
            properties: { _bab: box.handle, _bab_id: boxId, _bab_step: String(cfg.step) },
          };
          const plan = planFor(p);
          if (plan) item.selling_plan = plan;
          return item;
        });
      }

      // Recharge dynamically priced customizable bundle.
      if (!box.bundle || !box.bundle.id) throw new Error(this.strings.bundleMissing || 'Bundle not configured');
      const n = this.totalUnits(box);
      const sized = (box.bundle.variants || [])
        .map((bv) => Object.assign({ n: leadingInt(bv.t) }, bv))
        .filter((bv) => bv.n != null)
        .sort((a, b) => a.n - b.n);
      const bundleVariant = sized.find((bv) => bv.n === n) || sized.find((bv) => bv.n >= n) || sized[sized.length - 1] || box.bundle.variants[0];
      if (!bundleVariant) throw new Error(this.strings.bundleMissing || 'Bundle not configured');

      const bundle = {
        externalProductId: String(box.bundle.id),
        externalVariantId: String(bundleVariant.id),
        selections: lines.map(({ p, v, qty }) => {
          const sel = {
            collectionId: String(p.col),
            externalProductId: String(p.id),
            externalVariantId: String(v.id),
            quantity: qty,
          };
          const plan = planFor(p);
          if (plan) sel.sellingPlan = plan;
          return sel;
        }),
      };

      let items = null;
      await this.loadRechargeSdk();
      const sdk = window.recharge && window.recharge.bundle;
      if (sdk && typeof sdk.getDynamicBundleItems === 'function') {
        try {
          items = sdk.getDynamicBundleItems(bundle, box.bundle.handle);
        } catch (e) {
          items = null;
        }
      }
      if (!Array.isArray(items) || !items.length) {
        // Documented shape, generated locally when the SDK is unavailable.
        const rcId = `${nanoId(10)}:${box.bundle.id}`;
        items = bundle.selections.map((sel) => ({
          id: parseInt(sel.externalVariantId, 10),
          quantity: sel.quantity,
          properties: {
            _rc_bundle: rcId,
            _rc_bundle_variant: bundle.externalVariantId,
            _rc_bundle_parent: box.bundle.handle,
            _rc_bundle_collection_id: sel.collectionId,
          },
        }));
      }
      // Recharge requires the selling plan on every line when subscribing.
      const planByVariant = new Map(bundle.selections.map((sel) => [String(sel.externalVariantId), sel.sellingPlan]));
      items.forEach((item) => {
        const plan = planByVariant.get(String(item.id));
        if (plan && !item.selling_plan) item.selling_plan = plan;
        if (!item.quantity) item.quantity = 1;
      });
      return items;
    }

    loadRechargeSdk() {
      if (window.recharge && window.recharge.bundle) return Promise.resolve();
      if (!this.sdkUrl) return Promise.resolve();
      if (this.sdkPromise) return this.sdkPromise;
      this.sdkPromise = new Promise((resolve) => {
        const script = document.createElement('script');
        script.src = this.sdkUrl;
        script.async = true;
        const done = () => resolve();
        script.addEventListener('load', done);
        script.addEventListener('error', done);
        setTimeout(done, 8000);
        document.head.appendChild(script);
      });
      return this.sdkPromise;
    }

    // ----- Persistence ----------------------------------------------------

    storageKey(handle) {
      return `bab:${handle}`;
    }

    loadState(handle) {
      try {
        const raw = window.sessionStorage.getItem(this.storageKey(handle));
        return raw ? JSON.parse(raw) : null;
      } catch (e) {
        return null;
      }
    }

    saveState(box) {
      try {
        window.sessionStorage.setItem(this.storageKey(box.handle), JSON.stringify(box.state));
      } catch (e) {
        /* storage unavailable: the box simply is not remembered */
      }
    }

    // ----- Money ----------------------------------------------------------

    formatMoney(cents) {
      const value = Number(cents) || 0;
      const format = this.moneyFormat;
      const placeholder = (format.match(/\{\{\s*(\w+)\s*\}\}/) || [])[1] || 'amount';
      const withDelims = (num, precision, thousands, decimal) => {
        const fixed = (num / 100).toFixed(precision);
        const [whole, frac] = fixed.split('.');
        const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return frac ? `${grouped}${decimal}${frac}` : grouped;
      };
      let out;
      switch (placeholder) {
        case 'amount_no_decimals':
          out = withDelims(value, 0, ',', '.');
          break;
        case 'amount_with_comma_separator':
          out = withDelims(value, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          out = withDelims(value, 0, '.', ',');
          break;
        case 'amount_with_apostrophe_separator':
          out = withDelims(value, 2, "'", '.');
          break;
        default:
          out = withDelims(value, 2, ',', '.');
      }
      return format.replace(/\{\{\s*\w+\s*\}\}/, out).replace(/<[^>]*>/g, '');
    }
  }

  customElements.define('build-a-box', BuildABox);
}
